// /stake, native staking from the vault (docs/19-staking-plan.md 6.1 the
// flows, 5.3 the validator list, 6.3 the checks before signing, 3.6 the copy;
// ST6b). One screen for every staking step, chosen by the query string the
// overview's Staked rows link with: `?action=stake` (the default: choose a
// validator, an amount, review, confirm), `again&n=` (stake more into an
// unstaked account), `switch&n=` (another validator for an unstaked
// account), `part&n=` (unstake part), and `unstake`, `savings`, `collect`,
// `close` with `n=` (review, confirm). Review runs every check of docs/19 6.3
// without a key (engine.planStake); Confirm runs them again, read fresh,
// before the key is used, and then the pipeline every action shares (the
// four outcomes, the key refresh after a failure). Withdraw.tsx is the
// pattern for the review, the outcome and the refresh.

import type { VaultSigner } from '@thevault/sdk';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearch } from 'wouter';
import { KeyRefresh } from '../components/KeyRefresh';
import { Address, Amount, Button, Field, Notice, StatusLine } from '../components/primitives';
import { ReviewPanel, type ReviewRow } from '../components/Review';
import { Shell } from '../components/Shell';
import { SceneCard } from '../components/SceneWindow';
import { type ActionProgress, type ChainState, type CostEstimate, type StakeAction, type StakeHoldings, type StakePlan, type StakeResult, ActionFailed, ActionUnknown, InsufficientGas, RefreshRequired, StakeRefused, describe, retirementOf } from '../engine';
import { session, useSession } from '../session';
import { type PositionView, type ValidatorEntry, type VoteCheck, bpsText, readVote, validatorName, validatorsFor } from '../staking';
import { explorerBase, explorerUrl, parseUnits, shortSig } from '../util';

type Kind = StakeAction['kind'];
const KINDS: Kind[] = ['stake', 'again', 'unstake', 'part', 'savings', 'close', 'switch', 'collect'];

const TITLES: Record<Kind, { h1: string; button: string; review: string; confirm: string; net: string }> = {
  stake: { h1: 'Stake SOL', button: 'Review stake', review: 'Review your stake', confirm: 'Confirm stake', net: 'Stake' },
  again: { h1: 'Stake again', button: 'Review stake', review: 'Review your stake', confirm: 'Confirm stake', net: 'Stake' },
  unstake: { h1: 'Unstake', button: 'Review unstake', review: 'Review unstaking', confirm: 'Confirm unstake', net: 'Unstake' },
  part: { h1: 'Unstake part', button: 'Review unstake', review: 'Review unstaking part', confirm: 'Confirm unstake', net: 'Unstake' },
  savings: { h1: 'Move to savings', button: 'Review move', review: 'Review the move to savings', confirm: 'Confirm move', net: 'Move to savings' },
  close: { h1: 'Close the stake account', button: 'Review close', review: 'Review closing', confirm: 'Confirm close', net: 'Close and move' },
  switch: { h1: 'Switch validator', button: 'Review switch', review: 'Review the switch', confirm: 'Confirm switch', net: 'Stake with' },
  collect: { h1: 'Collect extra', button: 'Review collect', review: 'Review collecting', confirm: 'Confirm collect', net: 'Collect' },
};

function needsValidator(k: Kind): boolean {
  return k === 'stake' || k === 'again' || k === 'switch';
}
function needsAmount(k: Kind): boolean {
  return k === 'stake' || k === 'again' || k === 'part';
}

/** "about 9.4 hours" for slots at the slot clock's pace (docs/19 6.1: the estimate from getEpochInfo and the app's slot clock, which measures the pace once it has watched for a few seconds and assumes 400 ms before that). */
function durationText(slots: bigint, msPerSlot: number): string {
  const s = (Number(slots) * msPerSlot) / 1000;
  if (s < 90) return `about ${Math.max(1, Math.round(s))} seconds`;
  if (s < 5400) return `about ${Math.round(s / 60)} minutes`;
  return `about ${(s / 3600).toFixed(1)} hours`;
}

function TxLine({ signature, txBase }: { signature: string; txBase: string | null }) {
  const url = explorerUrl(txBase, signature);
  return (
    <p className="ink">
      {url ? (
        <>
          Transaction{' '}
          <a href={url} target="_blank" rel="noreferrer">
            {shortSig(signature)} ↗
          </a>
          .
        </>
      ) : (
        `Transaction ${shortSig(signature)}.`
      )}
    </p>
  );
}

/** One validator as the chooser shows it: the listed entry (if any) and what the chain says now. */
interface Candidate {
  vote: string;
  entry: ValidatorEntry | null;
  check: VoteCheck | null;
}

function ValidatorLine({ c }: { c: Candidate }) {
  const info = c.check?.info;
  return (
    <span className="stack tight">
      <span>{c.entry?.name ?? 'A validator you entered'}</span>
      <small className="mono">{c.vote}</small>
      <small>
        {c.check === null
          ? 'Reading it on chain…'
          : c.check.refusal
            ? c.check.refusal
            : `Commission ${bpsText(info!.commissionBps)} now; last voted in staking period ${c.check.lastEarned}.`}
      </small>
      {c.check?.warnings.map((w) => (
        <small key={w} className="ink">
          {w}
        </small>
      ))}
    </span>
  );
}

export function Stake() {
  const { settings } = useSession();
  const engine = session.engine();
  const params = new URLSearchParams(useSearch());
  const kindParam = params.get('action') as Kind | null;
  const kind: Kind = kindParam && KINDS.includes(kindParam) ? kindParam : 'stake';
  const nParam = params.get('n');
  const n = nParam !== null && /^\d+$/.test(nParam) ? Number(nParam) : null;
  const titles = TITLES[kind];

  const [state, setState] = useState<ChainState | null>(null);
  const [signer, setSigner] = useState<VaultSigner | null>(null);
  const [holdings, setHoldings] = useState<StakeHoldings | null>(null);
  const [gateLine, setGateLine] = useState<string | null>(null);
  const [epoch, setEpoch] = useState<bigint | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshNeeded, setRefreshNeeded] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [amountText, setAmountText] = useState('');

  const [plan, setPlan] = useState<StakePlan | null>(null);
  const [planning, setPlanning] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [cost, setCost] = useState<CostEstimate | null>(null);
  const [progress, setProgress] = useState<ActionProgress | null>(null);
  const [result, setResult] = useState<StakeResult | null>(null);
  const [unknown, setUnknown] = useState<{ signature: string; detail: string; note: string | null } | null>(null);
  const confirming = useRef(false);

  const position: PositionView | null = n !== null ? (holdings?.owned.find((p) => p.n === n) ?? null) : null;

  const reload = useCallback(async () => {
    try {
      const s = await engine.readChain();
      setState(s);
      if (s.kind === 'vault') {
        const sg = await engine.signerFor(s);
        setSigner(sg);
        const keys = await engine.keyStatus(sg);
        if (keys.unresolved.length) await engine.reconcile(sg);
        setRefreshNeeded(keys.refreshReason);
      }
      const [gate, h, info] = await Promise.all([engine.stakingGate(), engine.stakePositions(), engine.epochInfo()]);
      setGateLine(gate.on ? null : gate.reason);
      setHoldings(h);
      setEpoch(info.epoch);
    } catch (e) {
      setError(describe(e));
    }
  }, [engine]);
  useEffect(() => {
    void reload();
  }, [reload]);

  // The listed validators for this cluster, each checked on chain (docs/19 6.3 item 5); a pasted one is added on Check.
  useEffect(() => {
    if (!needsValidator(kind) || epoch === null) return;
    let cancelled = false;
    const listed = validatorsFor(settings.rpcUrl);
    setCandidates(listed.map((entry) => ({ vote: entry.voteAccount, entry, check: null })));
    void Promise.all(listed.map((entry) => readVote(engine.rpc, entry.voteAccount, epoch).catch((e) => ({ vote: entry.voteAccount, info: null, lastEarned: null, refusal: describe(e), warnings: [] }) as VoteCheck))).then((checks) => {
      if (cancelled) return;
      setCandidates((cs) => cs.map((c) => ({ ...c, check: checks.find((k) => k.vote === c.vote) ?? c.check })));
    });
    return () => {
      cancelled = true;
    };
  }, [engine, kind, epoch, settings.rpcUrl]);

  const checkPasted = async () => {
    const vote = pasted.trim();
    if (!vote || epoch === null) return;
    const check = await readVote(engine.rpc, vote, epoch).catch((e) => ({ vote, info: null, lastEarned: null, refusal: describe(e), warnings: [] }) as VoteCheck);
    setCandidates((cs) => [...cs.filter((c) => c.vote !== vote), { vote, entry: validatorsFor(settings.rpcUrl).find((v) => v.voteAccount === vote) ?? null, check }]);
    if (!check.refusal) setChosen(vote);
  };

  const amount = needsAmount(kind) ? parseUnits(amountText, 9) : null;
  const chosenCandidate = candidates.find((c) => c.vote === chosen) ?? null;
  const validatorReady = !needsValidator(kind) || (!!chosenCandidate?.check && !chosenCandidate.check.refusal);
  const amountReady = !needsAmount(kind) || (amount !== null && amount > 0n);
  const positionReady = kind === 'stake' || !!position;
  const canReview = !!signer && validatorReady && amountReady && positionReady && !planning && !gateLine;

  const actionOf = (): StakeAction | null => {
    switch (kind) {
      case 'stake':
        return chosen && amount ? { kind, vote: chosen, lamports: amount } : null;
      case 'again':
        return chosen && amount && n !== null ? { kind, n, vote: chosen, lamports: amount } : null;
      case 'switch':
        return chosen && n !== null ? { kind, n, vote: chosen } : null;
      case 'part':
        return amount && n !== null ? { kind, n, lamports: amount } : null;
      default:
        return n !== null ? ({ kind, n } as StakeAction) : null;
    }
  };

  const estimate = () => {
    setCost(null);
    void engine
      .stakeCost()
      .then(setCost)
      .catch((e) => setError(describe(e)));
  };

  const review = async () => {
    const action = actionOf();
    if (!action || !canReview) return;
    setPlanning(true);
    setRefusal(null);
    setProgress(null);
    setResult(null);
    setUnknown(null);
    try {
      const p = await engine.planStake(action);
      setPlan(p);
      estimate();
    } catch (e) {
      setRefusal(e instanceof StakeRefused ? e.message : `Nothing was signed: ${describe(e)}`);
    } finally {
      setPlanning(false);
    }
  };

  const confirm = async () => {
    if (!plan || !signer || confirming.current) return;
    confirming.current = true;
    setFailed(null);
    setUnknown(null);
    const a = plan.action;
    try {
      const r =
        a.kind === 'stake'
          ? await engine.stake(signer, a.vote, a.lamports, setProgress)
          : a.kind === 'again'
            ? await engine.stakeAgain(signer, a.n, a.vote, a.lamports, setProgress)
            : a.kind === 'unstake'
              ? await engine.unstake(signer, a.n, setProgress)
              : a.kind === 'part'
                ? await engine.unstakePart(signer, a.n, a.lamports, setProgress)
                : a.kind === 'savings'
                  ? await engine.moveToSavings(signer, a.n, setProgress)
                  : a.kind === 'close'
                    ? await engine.closeStake(signer, a.n, setProgress)
                    : a.kind === 'switch'
                      ? await engine.switchValidator(signer, a.n, a.vote, setProgress)
                      : await engine.collectExtra(signer, a.n, a.lamports, setProgress);
      setResult(r);
      await reload();
    } catch (err) {
      if (err instanceof RefreshRequired) {
        setProgress(null);
        setRefreshNeeded(err.reason);
      } else if (err instanceof ActionFailed) setFailed(`${err.message} ${err.detail}`);
      else if (err instanceof ActionUnknown) setUnknown({ signature: err.signature, detail: err.detail, note: null });
      else if (err instanceof InsufficientGas) {
        setProgress(null);
        setCost(err.cost);
      } else setProgress({ step: 'rejected', error: err instanceof StakeRefused ? err.message : describe(err) });
    } finally {
      confirming.current = false;
    }
  };

  const checkUnknown = async () => {
    if (!unknown || !signer) return;
    try {
      await engine.reconcile(signer);
      const record = (await engine.history(signer)).find((r) => r.txSignature === unknown.signature);
      if (record?.status === 'landed') {
        setProgress({ step: 'landed', leaf: record.leaf, signature: unknown.signature });
        setUnknown(null);
        await reload();
      } else if (record?.status === 'burned') {
        const retired = retirementOf(record);
        setUnknown(null);
        setProgress({ step: retired.step, leaf: record.leaf, signature: unknown.signature, error: retired.sentence });
        setFailed(retired.sentence);
      } else setUnknown({ ...unknown, note: 'Still unknown: the network has no confirmation yet. Check again in a moment.' });
    } catch (e) {
      setUnknown({ ...unknown, note: describe(e) });
    }
  };

  const reset = () => {
    setPlan(null);
    setCost(null);
    setProgress(null);
    setResult(null);
    setUnknown(null);
    setRefusal(null);
  };

  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const keyLine = signer ? `tree ${signer.treeIndex}` : 'this tree';
  const detail = state?.kind === 'vault' ? `KEY TREE ${state.snap.treeIndex}` : state ? state.kind.toUpperCase() : 'READING';
  const vaultReady = state?.kind === 'vault';
  const formsShown = vaultReady && !failed && !refreshNeeded && !gateLine;
  const msPerSlot = engine.clock.msPerSlot();

  // ---- the review's rows, per action
  const rows: ReviewRow[] = [];
  let net: { label: string; value: ReactNode; detail?: ReactNode } | undefined;
  if (plan) {
    const a = plan.action;
    const named = (vote: string) => validatorName(settings.rpcUrl, vote) ?? 'A validator you entered';
    const boundary = `At the end of the network’s current staking period, in ${durationText(plan.slotsLeftInEpoch, msPerSlot)}`;
    const boundaryNote = `An estimate, not a promise: ${plan.slotsLeftInEpoch.toLocaleString('en-US')} slots are left in this period. Slots are counted at about ${Math.round(msPerSlot)} ms each, the pace this network has shown recently (400 ms until the app has watched long enough).`;
    if (a.kind === 'stake' || a.kind === 'again') {
      net = { label: titles.net, value: <Amount raw={a.lamports} decimals={9} symbol="SOL" size="large" /> };
      rows.push({ group: 'What you stake', label: 'Validator', value: named(a.vote), detail: <span className="mono">{a.vote}</span> });
      rows.push({ group: 'What you stake', label: 'Commission', value: plan.vote?.info ? bpsText(plan.vote.info.commissionBps) : 'unknown', detail: 'What the validator keeps of the rewards, read from its vote account now. It can change it; the app warns you when it rises.' });
      rows.push({ group: 'What you stake', label: 'Starts earning', value: boundary, detail: `${boundaryNote} Rewards for a staking period are paid at the start of the next one, so your first reward arrives one period after the stake starts earning.` });
      rows.push({
        group: 'What you stake',
        label: 'From your vault',
        value: <Amount raw={plan.unwrap} decimals={9} symbol="SOL" maxFraction={9} />,
        detail: a.kind === 'stake' ? (
          <>
            The amount staked plus <Amount raw={plan.rent200} decimals={9} symbol="SOL" maxFraction={9} /> of rent for the stake account, read from the network now. The rent comes from your vault&rsquo;s own SOL, not the fee reserve; it stays in the stake account and comes back when you close it.
          </>
        ) : (
          'The stake account already holds its rent, so only the amount staked moves.'
        ),
      });
      rows.push({ group: 'What you stake', label: 'Smallest stake', value: <Amount raw={plan.minDelegation} decimals={9} symbol="SOL" />, detail: plan.minimumSource === 'simulated' ? 'Read from the Stake program now.' : 'Assumed: the Stake program gave no answer to the app’s question, so the network’s documented 1 SOL is used.' });
    } else if (a.kind === 'switch') {
      net = { label: titles.net, value: named(a.vote) };
      rows.push({ group: 'What changes', label: 'Validator', value: named(a.vote), detail: <span className="mono">{a.vote}</span> });
      rows.push({ group: 'What changes', label: 'Commission', value: plan.vote?.info ? bpsText(plan.vote.info.commissionBps) : 'unknown' });
      rows.push({ group: 'What changes', label: 'Amount staked', value: <Amount raw={plan.lamportsBefore - plan.rent200} decimals={9} symbol="SOL" />, detail: 'Nothing is withdrawn: the same stake account is staked with the new validator.' });
      rows.push({ group: 'What changes', label: 'Starts earning', value: boundary, detail: boundaryNote });
    } else if (a.kind === 'unstake') {
      net = { label: titles.net, value: <Amount raw={position?.delegated ?? 0n} decimals={9} symbol="SOL" size="large" /> };
      rows.push({ group: 'What changes', label: 'Stops earning', value: boundary, detail: `${boundaryNote} It can be moved to savings once that period has ended, or later if the network limits how much may unstake at once.` });
    } else if (a.kind === 'part') {
      net = { label: titles.net, value: <Amount raw={a.lamports} decimals={9} symbol="SOL" size="large" /> };
      rows.push({ group: 'What changes', label: 'Stays staked', value: <Amount raw={(position?.delegated ?? 0n) - a.lamports} decimals={9} symbol="SOL" /> });
      rows.push({ group: 'What changes', label: 'New stake account', value: <Amount raw={plan.rent200} decimals={9} symbol="SOL" maxFraction={9} />, detail: 'The unstaked part moves to a stake account of its own; its rent comes from your vault’s own SOL and comes back when it is closed.' });
      rows.push({ group: 'What changes', label: 'Stops earning', value: boundary, detail: boundaryNote });
    } else if (a.kind === 'savings' || a.kind === 'collect' || a.kind === 'close') {
      net = { label: titles.net, value: <Amount raw={plan.withdraw} decimals={9} symbol="SOL" size="large" /> };
      rows.push({ group: 'What changes', label: 'To', value: "Your vault's swappable SOL", detail: 'Back in the vault at once, in the same transaction.' });
      if (a.kind === 'savings') rows.push({ group: 'What changes', label: 'Stays in the stake account', value: <Amount raw={plan.rent200} decimals={9} symbol="SOL" maxFraction={9} />, detail: 'Its rent, so the account stays open and can be staked again. Close it to take the rent too.' });
      if (a.kind === 'collect') rows.push({ group: 'What changes', label: 'Stays staked', value: <Amount raw={position?.delegated ?? 0n} decimals={9} symbol="SOL" />, detail: 'Only what the stake account holds above its stake and rent moves.' });
    }
    rows.push({ group: 'Costs', label: 'Protocol fee', value: 'None', detail: 'The vault charges no fee on staking.' });
  }

  const positionLine = position ? (
    <p className="ink">
      Stake account {n}: <Amount raw={position.lamports} decimals={9} symbol="SOL" /> in it, {position.status.toLowerCase()}
      {position.vote ? `, with ${validatorName(settings.rpcUrl, position.vote) ?? 'a validator you entered'}` : ''}.
    </p>
  ) : null;

  return (
    <Shell number="08" title="Stake" detail={detail}>
      <div className={vaultReady ? 'task' : 'task single'}>
        <div className="task-main">
          <div className="composer-head">
            <h1>{titles.h1}</h1>
            <span className="dim">{plan ? 'Review' : 'From your vault'}</span>
          </div>
          <p className="task-intro">Your SOL is delegated from your vault to the validator you choose. Only your vault can withdraw it, and it can only come back to your vault. The validator cannot move your SOL; it decides how much you earn (its commission) and whether you earn at all. The vault&rsquo;s program is not yet audited.</p>

          {error ? (
            <StatusLine state="failed" word="Could not read the chain.">
              {error}
            </StatusLine>
          ) : null}
          {gateLine ? (
            <Notice label="Staking is off" tone="warn">
              <p className="ink">{gateLine}</p>
            </Notice>
          ) : null}
          {state && state.kind !== 'vault' ? (
            <Notice label="Not available">
              <p className="ink">{state.kind === 'absent' || state.kind === 'prefunded' ? 'Activate the vault from the overview before staking.' : 'The vault is not in a state that can stake.'}</p>
            </Notice>
          ) : null}
          {kind !== 'stake' && holdings && !position ? (
            <Notice label="Not found">
              <p className="ink">Your vault has no stake account {n ?? ''} that it controls alone. The overview lists the ones it has.</p>
            </Notice>
          ) : null}

          {refreshNeeded && signer ? (
            <Notice label="Key refresh" tone="warn">
              <KeyRefresh
                engine={engine}
                signer={signer}
                reason={`${refreshNeeded} It runs now, before anything else; staking follows.`}
                onDone={() => {
                  setRefreshNeeded(null);
                  setFailed(null);
                  reset();
                  void reload();
                }}
              />
            </Notice>
          ) : null}
          {failed && signer ? (
            <Notice label="Key refresh" tone="warn">
              <KeyRefresh
                engine={engine}
                signer={signer}
                reason={failed}
                onDone={() => {
                  setFailed(null);
                  reset();
                  void reload();
                }}
              />
            </Notice>
          ) : null}

          {formsShown && !plan ? (
            <form
              className="composer"
              onSubmit={(e) => {
                e.preventDefault();
                void review();
              }}
            >
              {positionLine}
              {kind === 'close' ? (
                <Notice label="Before you close" tone="warn">
                  <p className="ink">Closing takes everything, the rent included, and the account stops existing. If anyone sends SOL to this stake account before the close lands, it fails on chain and its one-time key is retired. Move to savings keeps the account open and cannot fail that way.</p>
                </Notice>
              ) : null}
              {needsValidator(kind) ? (
                <Field id="stake-validator" label="Validator">
                  <div className="stack">
                    {candidates.length === 0 ? <p>No validators are listed for this network yet. Enter a vote account address below.</p> : null}
                    {candidates.map((c) => (
                      <label key={c.vote} className="radio">
                        <input type="radio" name="stake-validator" value={c.vote} checked={chosen === c.vote} disabled={!c.check || !!c.check.refusal} onChange={() => setChosen(c.vote)} aria-label={c.entry?.name ?? c.vote} />
                        <ValidatorLine c={c} />
                      </label>
                    ))}
                    <div className="field-row">
                      <input id="stake-validator" className="input" value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="Another validator's vote account address" autoComplete="off" spellCheck={false} aria-label="Another validator's vote account address" />
                      <Button variant="ghost" small type="button" disabled={!pasted.trim() || epoch === null} onClick={() => void checkPasted()}>
                        Check
                      </Button>
                    </div>
                    <small className="help">Any validator may be chosen; the app checks on chain that it is a vote account that has been voting.</small>
                  </div>
                </Field>
              ) : null}
              {needsAmount(kind) ? (
                <Field
                  id="stake-amount"
                  label={kind === 'part' ? 'Amount to unstake' : 'Amount to stake'}
                  help={
                    kind === 'part' && position ? (
                      <>
                        Of <Amount raw={position.delegated} decimals={9} symbol="SOL" /> staked. Both parts keep at least 1 SOL staked.
                      </>
                    ) : kind === 'stake' ? (
                      'At least 1 SOL. The stake account also keeps a small rent from your vault, shown in the review.'
                    ) : undefined
                  }
                >
                  <input id="stake-amount" className="input" inputMode="decimal" value={amountText} onChange={(e) => setAmountText(e.target.value)} placeholder="0.00" autoComplete="off" />
                </Field>
              ) : null}
              {refusal ? (
                <StatusLine state="failed" word="Not signed.">
                  {refusal}
                </StatusLine>
              ) : null}
              <Button type="submit" block disabled={!canReview} working={planning}>
                {planning ? 'Checking' : titles.button}
              </Button>
            </form>
          ) : null}

          {formsShown && plan ? (
            <ReviewPanel
              engine={engine}
              title={titles.review}
              rpcUrl={settings.rpcUrl}
              keyLine={keyLine}
              cost={cost}
              progress={progress}
              confirmLabel={titles.confirm}
              onConfirm={() => void confirm()}
              onCancel={result || unknown ? undefined : reset}
              onRecheck={estimate}
              disabled={!signer || !!unknown}
              net={net}
              rows={rows}
            >
              {kind === 'close' && !result ? (
                <Notice label="Before you close" tone="warn">
                  <p className="ink">If anyone sends SOL to this stake account before the close lands, it fails on chain and its one-time key is retired.</p>
                </Notice>
              ) : null}
              {unknown ? (
                <Notice label="Outcome unknown" tone="warn">
                  <p className="ink">{unknown.note ?? `${unknown.detail} Nothing is retired and nothing new is signed until the outcome is known.`}</p>
                  <div className="actions">
                    <Button small onClick={() => void checkUnknown()}>
                      Check again
                    </Button>
                  </div>
                </Notice>
              ) : null}
              {result ? (
                <Notice label="Landed" tone="accent">
                  <TxLine signature={result.signature} txBase={txBase} />
                  <div className="actions">
                    <Link href="/vault" className="button small">
                      Back to overview
                    </Link>
                    {kind === 'savings' ? (
                      <Link href={`/stake?action=again&n=${result.plan.n}`} className="button small ghost" onClick={reset}>
                        Stake again into this account
                      </Link>
                    ) : null}
                  </div>
                </Notice>
              ) : null}
            </ReviewPanel>
          ) : null}
        </div>

        {vaultReady ? (
          <aside className="task-side" aria-label="About staking">
            <SceneCard scene="stake" />
            <div className="panel">
              <span className="eyebrow">What it costs</span>
              <p>There is no protocol fee on staking.</p>
              <p>The network fee comes from your fee reserve. A new stake account&rsquo;s rent comes from your vault&rsquo;s own SOL and comes back when the account is closed.</p>
            </div>
            <div className="panel">
              <span className="eyebrow">What the vault protects</span>
              <p>The Stake program has no single key that can change it; it changes only through upgrades adopted by the Solana network as a whole. If a validator stops voting for about a week, anyone can start unstaking that stake; only your vault can then move the SOL. A future network upgrade could pause staking actions in the app until it supports them; your SOL stays in the stake account meanwhile.</p>
            </div>
          </aside>
        ) : null}
      </div>
    </Shell>
  );
}
